import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCostsApi } from "../../http/costs/api";
import { UpsertCost } from "../../http/costs/types";
import { convertLocalDateToUTC } from "../../types/utils";
import ModalDialog from "../ui/ModalDialog";
import CostForm, { CostFormBody } from "./CostForm";

type EditCostDialogProps = {
  costId: string;
  onClose: () => void;
  carId?: string;
};

const EditCostDialog: FC<EditCostDialogProps> = ({ costId, onClose, carId }: EditCostDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const costsApi = useAdminCostsApi();

  const costSwr = costsApi.getAdminCost(costId, aborter.current.signal);
  const [cost, isInitialized] = useDataInitializer(costSwr.data, costSwr.error);

  useEffect(() => {
    costId && costSwr.trigger();
  }, [costId]);

  useEffect(() => {
    costSwr.error && dispatch(showErrorNotification(i18n, costSwr.error));
  }, [costSwr.error]);

  const onSubmit = async (data: CostFormBody) => {
    if (cost) {
      const date = convertLocalDateToUTC(data.date);

      const body: UpsertCost = {
        carId: carId ?? data.car?.id!,
        typeId: data.type.id,
        date: date,
        costNet: data.costNet!,
        costGross: data.costGross!,
        currentVersionNumber: cost.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await costsApi.updateAdminCost(cost.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <ModalDialog onClose={onClose} title={i18n.t.costs.edit}>
        <CostForm
          onSubmit={onSubmit}
          isDisabled={!userSettings.permissions.costs.update || isSubmitting || !cost?.id}
          isLoading={!isInitialized}
          cost={cost}
          allowSelectCar={!carId}
        />
      </ModalDialog>
    </>
  );
};
export default EditCostDialog;
